'use client';

import type { FC } from 'react';
import type { BreadcrumbItemType } from '@/components/shared';
import type { ProviderDto } from '@/queries/providers';
import { useTranslations } from 'next-intl';
import { Suspense, useState } from 'react';
import { AuthorCard } from '@/components/entities/author/AuthorCard';
import { GamesSearchBar } from '@/components/entities/games/GamesSearchBar';
import { ProviderBanner } from '@/components/entities/providers/ProviderBanner';
import { SeoPagesTopSidebar } from '@/components/entities/seoPages/SeoPagesTopSidebar';
import { FilteredGameList } from '@/components/features/seo-games/seo-game-list/FilteredGameList';
import { AppBreadcrumbs } from '@/components/shared';
import { FaqAccordionSection } from '@/components/ui/Accordion/FaqAccordion';
import { AppRoutes } from '@/i18n/constants';
import { getPlateContentState } from '@/utils/parsing.utils';
import { AboutProvider } from '../../_components/AboutProvider';

interface ProviderViewProps {
  provider: ProviderDto;
  page: number;
}

export const ProviderView: FC<ProviderViewProps> = ({ provider, page }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const content = provider?.pageContent?.body;
  const { value: plateContentValue, hasTextContent } = getPlateContentState(content);

  const t = useTranslations('providers.game-list');

  const breadcrumbsItems: BreadcrumbItemType[] = [
    { href: '/', label: 'All games', icon: 'all-games' },
    { label: 'Providers', href: '/providers' },
    { label: provider.name },
  ];

  return (
    <div className="max-w-282 justify-center flex gap-13 w-full mx-auto mb-6 md:mb-0">
      <div className="max-w-218.5 w-full xxl:shrink-0 md:mb-10">
        <AppBreadcrumbs items={breadcrumbsItems} className="lg:sr-only h-0" />

        <div className="mb-13 md:mb-17">
          <ProviderBanner provider={provider} className="mb-5.75 md:mb-9.75" />

          <hgroup className="w-full flex flex-col mb-12.5 md:mb-5.75">
            <div className="relative w-full flex justify-between items-start">

              <div>
                <h1 className="text-[2rem] md:text-[1.75rem] font-medium tracking-[-6%] text-gradient bg-promo-banner-title-gradient">{provider.name}</h1>
                {/* TODO: add rating when it will be available */}
                {/* <RatingStars rating={5} size="md" interactive={false} maxRating={5} showValue className="mt-0.75 mb-1.5 md:mt-1.75 md:mb-1" /> */}
              </div>
              <GamesSearchBar
                placeholder={t('search-placeholder')}
                onSearchChange={setSearchQuery}
              />
            </div>

            {provider?.description && (
              <div className="text-lg leading-7 mt-1.75 text-c300">
                { provider.description }
              </div>
            )}
          </hgroup>

          <Suspense>
            <FilteredGameList
              page={page}
              search={searchQuery}
              gridClassName="mt-5"
              providerId={provider.id}
              className="max-w-218 w-full"
              pageHrefBase={AppRoutes.providersSlug(provider.alias)}
            />
          </Suspense>
        </div>

        <div className="flex flex-col gap-13 md:gap-17">

          { hasTextContent && <AboutProvider contentValue={plateContentValue} isDetailedPage className="[&>h3]:mb-4.25" /> }

          <SeoPagesTopSidebar className="xl:hidden grid grid-cols-2 gap-3" />

          {
            provider?.pageContent?.faqs && provider.pageContent.faqs.length > 0 && (
              <FaqAccordionSection
                className="max-w-200"
                faqsList={provider.pageContent.faqs}
              />
            )
          }

          {provider?.pageContent?.author && <AuthorCard className="max-w-200 md:mb-6" author={provider.pageContent.author} />}
        </div>
      </div>
      <SeoPagesTopSidebar className="w-full hidden xl:flex flex-col mt-0.5 gap-10 max-w-50 sticky top-0 self-start" />
    </div>
  );
};
